import React, { useCallback, useMemo } from 'react'

import * as SliderPrimitive from '@radix-ui/react-slider'

import { NumberFormat } from 'src/utils/number'

import HStack from '../Flex/HStack'
import FormControl from '../FormControl/FormControl'
import { FormControlProps } from '../FormControl/util'
import NumberInput from '../Input/NumberInput'

type Props = FormControlProps & {
  max?: number
  min?: number
  onValueChange: (value: number) => void
  suffix?: string
  value: number
}
const Slider = ({
  max = 100,
  min = 0,
  onValueChange,
  suffix = '%',
  value,
  width = '100%',
  ...formControlProps
}: Props) => {
  const arrayValue = useMemo(() => [value], [value])
  const handleValueChange = useCallback(
    (newValue: number[] | number | undefined) => {
      if (!newValue) {
        onValueChange(min)
      } else if (Array.isArray(newValue)) {
        onValueChange(newValue[0] ?? min)
      } else {
        onValueChange(newValue)
      }
    },
    [min, onValueChange]
  )

  // Calculate input width based on max value digits plus suffix length
  const inputWidth = useMemo(() => {
    const maxDigits = String(max).length
    const suffixLength = suffix.length
    // Base width for number input plus extra space for suffix
    return `${50 + maxDigits * 10 + suffixLength * 8}px`
  }, [max, suffix])

  return (
    <FormControl {...formControlProps} width={width}>
      {({ ariaDescribedBy, ariaInvalid, ariaLive }) => (
        <HStack gap={2}>
          <SliderPrimitive.Root
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            aria-live={ariaLive}
            className="relative flex w-full touch-none items-center pt-1 select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col"
            data-slot="slider"
            id={formControlProps.id}
            max={max}
            min={min}
            onValueChange={handleValueChange}
            step={1}
            value={arrayValue}
          >
            <SliderPrimitive.Track
              data-slot="slider-track"
              className="relative grow overflow-hidden rounded-full bg-neutral-200 data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5"
            >
              <SliderPrimitive.Range
                data-slot="slider-range"
                className="absolute bg-sky-500 data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full"
              />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb
              data-slot="slider-thumb"
              className="ring-ring/50 block size-4 shrink-0 cursor-grab rounded-full border border-sky-500 bg-white shadow-sm ring-sky-200 transition-[color,box-shadow] hover:ring-2 focus-visible:ring-2 focus-visible:outline-hidden active:cursor-grabbing disabled:pointer-events-none disabled:opacity-50"
            />
          </SliderPrimitive.Root>
          <NumberInput
            value={value}
            onValueChange={handleValueChange}
            ariaLabel="Slider value"
            format={NumberFormat.Integer}
            max={max}
            min={min}
            width={inputWidth}
            textAlign="right"
            leftAddon={null}
            suffix={suffix}
          />
        </HStack>
      )}
    </FormControl>
  )
}

export default React.memo(Slider)
